import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import '../../components/tracker/Tracker.css';
import '../../components/tracker/PlayerDashboard.css';

// ─────────────────────────────────────────────
// PITCH LISTS
// ─────────────────────────────────────────────

const BASEBALL_PITCHES = [
  { code: '4S', label: '4-Seam FB' },
  { code: '2S', label: '2-Seam FB' },
  { code: 'SI', label: 'Sinker' },
  { code: 'CT', label: 'Cutter' },
  { code: 'CB', label: 'Curveball' },
  { code: 'SL', label: 'Slider' },
  { code: 'SW', label: 'Sweeper' },
  { code: 'SLV', label: 'Slurve' },
  { code: 'CH', label: 'Changeup' },
  { code: 'SP', label: 'Splitter' },
  { code: 'FK', label: 'Forkball' },
  { code: 'SC', label: 'Screwball' },
  { code: 'KN', label: 'Knuckleball' },
  { code: 'EP', label: 'Eephus' },
];

const SOFTBALL_PITCHES = [
  { code: 'FB', label: 'Fastball' },
  { code: 'RB', label: 'Rise Ball' },
  { code: 'DB', label: 'Drop Ball' },
  { code: 'CB', label: 'Curveball' },
  { code: 'SC', label: 'Screwball' },
  { code: 'CH', label: 'Changeup' },
  { code: 'KN', label: 'Knuckleball' },
  { code: 'OB', label: 'Off-speed / Blooper' },
  { code: 'DRV', label: 'Drop Curve' },
  { code: 'IB', label: 'Inside Pitch' },
];

const ALL_POSITIONS = [
  'P',
  'C',
  '1B',
  '2B',
  '3B',
  'SS',
  'LF',
  'CF',
  'RF',
  'DH',
];

const GRAD_YEARS = Array.from({ length: 12 }, (_, i) => {
  const year = new Date().getFullYear() + i;
  return year;
});

const TEAM_TYPES = [
  {
    group: 'School / Academic',
    types: ['High School', 'Middle School', 'College', 'Junior College'],
  },
  {
    group: 'Travel / Club',
    types: [
      'Travel/Club',
      'USSSA',
      'Perfect Game',
      'Triple Crown',
      'Prep Baseball Report (PBR)',
    ],
  },
  {
    group: 'Showcase',
    types: ['Showcase', 'Tournament Team', 'All-Star Team'],
  },
  {
    group: 'Community / League',
    types: [
      'Little League',
      'Pony League',
      'American Legion',
      'Babe Ruth',
      'Cal Ripken',
      'Dixie Youth',
      'Rec League',
      'Fall Ball',
      'Winter Ball',
    ],
  },
  {
    group: 'Other',
    types: ['Instructional League', 'Independent Team', 'Select Team'],
  },
];

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

export default function PlayerProfilePage() {
  const navigate = useNavigate();
  const { id } = useParams(); // present on edit, undefined on new
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Form fields
  const [firstName, setFirstName] = useState('');
  // Teams
  const [teams, setTeams] = useState([]);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamType, setNewTeamType] = useState('High School');
  const [newTeamSeason, setNewTeamSeason] = useState('');
  const [activeSeasons, setActiveSeasons] = useState([]);
  const [newTeamMembership, setNewTeamMembership] = useState('full_time');
  const [newTeamNotes, setNewTeamNotes] = useState('');
  const [lastName, setLastName] = useState('');
  const [sport, setSport] = useState('baseball');
  const [gradYear, setGradYear] = useState('');
  const [playingLevel, setPlayingLevel] = useState('');
  const [positions, setPositions] = useState([]);
  const [isPitcher, setIsPitcher] = useState(false);
  const [isPitcherOnly, setIsPitcherOnly] = useState(false);
  const [throws, setThrows] = useState('');
  const [bats, setBats] = useState('');
  const [playerNotes, setPlayerNotes] = useState('');
  const [pitches, setPitches] = useState([]);

  // Load existing player on edit
  useEffect(() => {
    if (!isEdit) return;
    async function fetchPlayer() {
      try {
        const res = await api.get(`/analytics/players/${id}`);
        const p = res.data;
        setFirstName(p.firstName || '');
        setLastName(p.lastName || '');
        setSport(p.sport || 'baseball');
        setGradYear(p.gradYear ? String(p.gradYear) : '');
        setPlayingLevel(p.playingLevel || '');
        setPositions(p.positions || []);
        setIsPitcher(p.isPitcher || false);
        setIsPitcherOnly(p.isPitcherOnly || false);
        setThrows(p.throws || '');
        setBats(p.bats || '');
        setPlayerNotes(p.playerNotes || '');
        setPitches(p.pitches || []);
        setTeams(p.teams || []);
        setCurrentTeamId(p.currentTeamId || null);
      } catch (err) {
        console.error('Failed to load player:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPlayer();
  }, [id, isEdit]);

  // When sport changes, clear pitches that don't belong to the new sport
  useEffect(() => {
    const validCodes = pitchListForSport(sport).map((p) => p.code);
    setPitches((prev) => prev.filter((c) => validCodes.includes(c)));
  }, [sport]);
  // Fetch active seasons for the team-add dropdown
  useEffect(() => {
    api.get('/seasons/active').then((res) => {
      setActiveSeasons(res.data || []);
    }).catch((err) => {
      console.error('Error fetching active seasons:', err);
    });
  }, []);

  function pitchListForSport(s) {
    return s === 'softball' ? SOFTBALL_PITCHES : BASEBALL_PITCHES;
  }

  function togglePosition(pos) {
    setPositions((prev) => {
      const next = prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos];
      if (pos === 'P') {
        const nowHasP = next.includes('P');
        setIsPitcher(nowHasP);
        if (!nowHasP) {
          setIsPitcherOnly(false);
          setPitches([]);
        }
      }
      return next;
    });
  }

  function togglePitch(code) {
    setPitches((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }

  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  function openEditTeam(team) {
    setEditingTeamId(team.id);
    setNewTeamName(team.name);
    setNewTeamType(team.type);
    setNewTeamSeason(team.season || '');
    setNewTeamMembership(team.membership);
    setNewTeamNotes(team.notes || '');
    setShowAddTeam(true);
  }

  function handleSaveTeam() {
    if (!newTeamName.trim()) return;
    if (editingTeamId) {
      setTeams((prev) =>
        prev.map((t) =>
          t.id === editingTeamId
            ? {
                ...t,
                name: newTeamName.trim(),
                type: newTeamType,
                season: newTeamSeason.trim() || null,
                membership: newTeamMembership,
                notes: newTeamNotes.trim() || null,
              }
            : t
        )
      );
      setEditingTeamId(null);
      setNewTeamName('');
      setNewTeamType('High School');
      setNewTeamSeason('');
      setNewTeamMembership('full_time');
      setNewTeamNotes('');
      setShowAddTeam(false);
    } else {
      handleAddTeam();
    }
  }

  function handleAddTeam() {
    if (!newTeamName.trim()) return;
    const team = {
      id: generateId(),
      name: newTeamName.trim(),
      type: newTeamType,
      season: newTeamSeason.trim() || null,
      membership: newTeamMembership,
      notes: newTeamNotes.trim() || null,
    };
    const updated = [...teams, team];
    setTeams(updated);
    // Auto-set as current if first team
    if (updated.length === 1) setCurrentTeamId(team.id);
    setNewTeamName('');
    setNewTeamType('High School');
    setNewTeamSeason('');
    setNewTeamMembership('full_time');
    setNewTeamNotes('');
    setShowAddTeam(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    setSaveError(null);
    const finalTeams = teams.filter((t) => !pendingDeleteIds.includes(t.id));
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      sport,
      gradYear: gradYear ? parseInt(gradYear) : null,
      playingLevel: playingLevel || null,
      positions,
      isPitcher,
      isPitcherOnly: isPitcher ? isPitcherOnly : false,
      pitches: isPitcher ? pitches : [],
      teams: finalTeams,
      currentTeamId,
      throws: throws || null,
      bats: bats || null,
      playerNotes: playerNotes || null,
    };

    try {
      if (isEdit) {
        await api.put(`/analytics/players/${id}`, payload);
      } else {
        await api.post('/analytics/players', payload);
      }
      navigate('/analytics/players');
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="dash-page">
        <div className="analytics-page-wrap">
          <div className="analytics-empty">Loading player…</div>
        </div>
      </div>
    );
  }

  const pitchList = pitchListForSport(sport);

  return (
    <div className="dash-page">
      <div className="analytics-page-wrap">
        {/* Header */}
        <div className="analytics-page-header">
          <button
            className="analytics-back-btn"
            onClick={() => navigate('/analytics/players')}
          >
            ← Players
          </button>
          <h1 className="analytics-page-title">
            {isEdit ? 'Edit Player' : 'New Player'}
          </h1>
          <div style={{ width: '80px' }} />
        </div>

        <form onSubmit={handleSubmit} className="player-profile-form">
          {/* ── Name ── */}
          <div className="profile-section">
            <div className="profile-field-row">
              <div className="profile-field">
                <label>First Name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder=""
                  required
                />
              </div>
              <div className="profile-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder=""
                  required
                />
              </div>
            </div>
          </div>

          {/* ── Sport ── */}
          <div className="profile-section">
            <div className="profile-section-label">Sport</div>
            <div className="profile-toggle-row">
              {['baseball', 'softball'].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`profile-toggle-btn ${sport === s ? 'active' : ''}`}
                  onClick={() => setSport(s)}
                >
                  {s === 'baseball' ? '⚾ Baseball' : '🥎 Softball'}
                </button>
              ))}
            </div>
          </div>

          {/* ── Grad Year ── */}
          <div className="profile-section">
            <div className="profile-field">
              <label>
                Grad Year <span className="optional">(optional)</span>
              </label>
              <select
                value={gradYear}
                onChange={(e) => setGradYear(e.target.value)}
              >
                <option value="">— Select —</option>
                {GRAD_YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Playing Level ── */}
          <div className="profile-section">
            <div className="profile-field">
              <label>
                Playing Level <span className="optional">(optional)</span>
              </label>
              <select
                id="playingLevel"
                value={playingLevel}
                onChange={(e) => setPlayingLevel(e.target.value)}
              >
                <option value="">Select level...</option>
                <optgroup label="Youth">
                  <option value="8U">8U</option>
                  <option value="9U">9U</option>
                  <option value="10U">10U</option>
                  <option value="11U">11U</option>
                  <option value="12U">12U</option>
                  <option value="13U">13U</option>
                  <option value="14U">14U</option>
                  <option value="15U">15U</option>
                  <option value="16U">16U</option>
                  <option value="18U">18U</option>
                </optgroup>
                <optgroup label="Middle School">
                  <option value="6th Grade">6th Grade</option>
                  <option value="7th Grade">7th Grade</option>
                  <option value="8th Grade">8th Grade</option>
                </optgroup>
                <optgroup label="High School">
                  <option value="Freshman">Freshman</option>
                  <option value="JV2">JV2</option>
                  <option value="JV">JV</option>
                  <option value="Varsity">Varsity</option>
                </optgroup>
                <optgroup label="College">
                  <option value="College">College</option>
                </optgroup>
              </select>
            </div>
          </div>

          {/* ── Throws / Bats ── */}
          <div className="profile-section">
            <div className="profile-section-label">Throws / Bats <span className="optional">(optional)</span></div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div className="profile-field" style={{ flex: 1 }}>
                <label>Throws</label>
                <select value={throws} onChange={(e) => setThrows(e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                  <option value="switch">Switch</option>
                </select>
              </div>
              <div className="profile-field" style={{ flex: 1 }}>
                <label>Bats</label>
                <select value={bats} onChange={(e) => setBats(e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                  <option value="switch">Switch</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Player Notes ── */}
          <div className="profile-section">
            <div className="profile-field">
              <label>
                General Player Notes <span className="optional">(optional)</span>
              </label>
              <p className="profile-field-hint">
                Ongoing context about your player that doesn't change season to season — the AI Coach uses this to tailor advice.
              </p>
              <textarea
                value={playerNotes}
                onChange={(e) => setPlayerNotes(e.target.value)}
                placeholder="Learning style, personality, long-term goals, physical considerations, position focus, etc."
                rows={3}
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-1)' }}
              />
            </div>
          </div>

          {/* ── Positions ── */}
          <div className="profile-section">
            <div className="profile-section-label">
              Positions{' '}
              <span className="optional">(select all that apply)</span>
            </div>
            <div className="profile-chip-grid">
              {ALL_POSITIONS.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  className={`profile-chip ${positions.includes(pos) ? 'active' : ''}`}
                  onClick={() => togglePosition(pos)}
                >
                  {pos}
                </button>
              ))}
            </div>
            {/* ── Inline pitcher fields when P is selected ── */}
            {isPitcher && (
              <div className="profile-pitcher-inline">
                {positions.length === 1 && positions[0] === 'P' && (
                  <div className="profile-toggle-field">
                    <div className="profile-toggle-info">
                      <div className="profile-toggle-title">Pitcher Only (PO)?</div>
                      <div className="profile-toggle-sub">
                        Only the Pitching tab will show during tracking.
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`profile-bool-btn ${isPitcherOnly ? 'on' : 'off'}`}
                      onClick={() => setIsPitcherOnly((prev) => !prev)}
                    >
                      {isPitcherOnly ? 'Yes — PO' : 'No'}
                    </button>
                  </div>
                )}
                <div className="profile-pitch-label">
                  What pitches do they throw?{' '}
                  <span className="optional">(select all that apply)</span>
                </div>
                <div className="profile-pitch-grid">
                  {pitchList.map(({ code, label }) => (
                    <button
                      key={code}
                      type="button"
                      className={`profile-pitch-btn ${pitches.includes(code) ? 'active' : ''}`}
                      onClick={() => togglePitch(code)}
                    >
                      <span className="pitch-code">{code}</span>
                      <span className="pitch-label">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Teams ── */}
          <div className="profile-section">
            <div className="profile-section-label">
              Teams <span className="optional">(track stats by team)</span>
            </div>

            {/* Existing teams */}
            {teams.length > 0 && (
              <div className="team-list">
                {teams.map((team) => {
                  if (pendingDeleteIds.includes(team.id)) {
                    return (
                      <div
                        key={team.id}
                        className="team-item team-item-pending-delete"
                      >
                        <div className="team-item-info">
                          <div className="team-item-name">
                            {team.name}
                          </div>
                          <div className="team-item-meta team-pending-delete-warning">
                            ⚠️ Click Save Changes to finalize the deletion of this team.
                          </div>
                        </div>
                        <div className="team-item-actions">
                          <button
                            type="button"
                            className="team-undo-delete-btn"
                            onClick={() =>
                              setPendingDeleteIds((prev) =>
                                prev.filter((id) => id !== team.id)
                              )
                            }
                          >
                            Undo Delete
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={team.id}
                      className={`team-item ${currentTeamId === team.id ? 'current' : ''}`}
                    >
                      <div className="team-item-info">
                        <div className="team-item-name">
                          {team.name}
                          {currentTeamId === team.id && (
                            <span className="team-current-badge">Current</span>
                          )}
                          <span
                            className={`team-membership-badge ${team.membership}`}
                          >
                            {team.membership === 'full_time'
                              ? 'Full Time'
                              : 'Guest'}
                          </span>
                        </div>
                        <div className="team-item-meta">
                          <span>{team.type}</span>
                          {team.season && <span> · {team.season}</span>}
                        </div>
                      </div>
                      <div className="team-item-actions">
                        {currentTeamId !== team.id && (
                          <button
                            type="button"
                            className="team-set-current-btn"
                            onClick={() => setCurrentTeamId(team.id)}
                          >
                            Set Current
                          </button>
                        )}
                        <button
                          type="button"
                          className="team-edit-btn"
                          onClick={() => openEditTeam(team)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="team-remove-btn"
                          onClick={() => setTeamToDelete(team)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add team form */}
            {showAddTeam ? (
              <div className="team-add-form">
                <div className="profile-field">
                  <label>Team Name *</label>
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder=""
                    autoFocus
                  />
                </div>
                <div className="profile-field">
                  <label>Team Type</label>
                  <select
                    value={newTeamType}
                    onChange={(e) => setNewTeamType(e.target.value)}
                  >
                    {TEAM_TYPES.map((group) => (
                      <optgroup key={group.group} label={group.group}>
                        {group.types.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="profile-field">
                  <label>
                    Season <span className="optional">(optional)</span>
                  </label>
                  <select
                    value={newTeamSeason}
                    onChange={(e) => setNewTeamSeason(e.target.value)}
                  >
                    <option value="">Select season</option>
                    {activeSeasons.map((season) => (
                      <option key={season.id} value={season.name}>
                        {season.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="profile-field">
                  <label>Membership</label>
                  <div className="profile-toggle-row">
                    {[
                      { value: 'full_time', label: 'Full Time' },
                      { value: 'guest', label: 'Guest Player' },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        className={`profile-toggle-btn ${newTeamMembership === value ? 'active' : ''}`}
                        onClick={() => setNewTeamMembership(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="profile-field">
                  <label>
                    Season Notes <span className="optional">(optional)</span>
                  </label>
                  <p className="profile-field-hint">
                    Context specific to this team and season — injuries, pitch counts, mechanical changes, anything the AI Coach should weigh when looking at this period.
                  </p>
                  <textarea
                    value={newTeamNotes}
                    onChange={(e) => setNewTeamNotes(e.target.value)}
                    placeholder="e.g. Dealing with shoulder soreness this summer, eased back on velocity work"
                    rows={3}
                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-1)' }}
                  />
                </div>
                <div className="team-add-form-actions">
                  <button
                    type="button"
                    className="team-add-confirm-btn"
                    onClick={handleSaveTeam}
                    disabled={!newTeamName.trim()}
                  >
                    {editingTeamId ? 'Save Changes' : 'Add Team'}
                  </button>
                  <button
                    type="button"
                    className="team-add-cancel-btn"
                    onClick={() => {
                      setShowAddTeam(false);
                      setEditingTeamId(null);
                      setNewTeamName('');
                      setNewTeamType('High School');
                      setNewTeamSeason('');
                      setNewTeamMembership('full_time');
                      setNewTeamNotes('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="team-add-btn"
                onClick={() => setShowAddTeam(true)}
              >
                + Add Team
              </button>
            )}
            {teamToDelete && (() => {
              const activeTeams = teams.filter((t) => !pendingDeleteIds.includes(t.id));
              const isOnlyTeam = activeTeams.length <= 1;
              const isCurrentTeam = currentTeamId === teamToDelete.id;

              if (isOnlyTeam) {
                return (
                  <div className="modal-overlay" onClick={() => setTeamToDelete(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h3>Cannot Remove Team</h3>
                      <p>
                        You must have at least one team. Add another team before removing this one.
                      </p>
                      <div className="modal-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setTeamToDelete(null)}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              if (isCurrentTeam) {
                return (
                  <div className="modal-overlay" onClick={() => setTeamToDelete(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h3>Cannot Remove Current Team</h3>
                      <p>
                        This is your current team. To delete, you must select another team as your current team. At least 1 team is always required.
                      </p>
                      <div className="modal-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setTeamToDelete(null)}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="modal-overlay" onClick={() => setTeamToDelete(null)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <h3>Remove Team?</h3>
                    <p>
                      Are you sure you want to remove <strong>{teamToDelete.name}</strong> from this player's profile?
                    </p>
                    <div className="modal-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setTeamToDelete(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                          setPendingDeleteIds((prev) => [...prev, teamToDelete.id]);
                          setTeamToDelete(null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>


          {/* ── Error ── */}
          {saveError && <div className="profile-save-error">{saveError}</div>}

          {/* ── Submit ── */}
          <div className="profile-submit-wrap">
            <button
              type="submit"
              className="profile-submit-btn"
              disabled={saving || !firstName.trim() || !lastName.trim()}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Player'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
